<template>
  <div v-if="showEdit">
    <v-infinite-scroll color="black" mode="manual" style="height: 100%; max-height: 400px" @load="loadMore">
      <v-radio-group v-model="internalModelValue">
        <v-list density="compact" class="bg-grey-lighten-4">
          <v-list-item v-for="option in allTemplateNames" :key="option.value" @click="selectTemplate(option)">
            <v-list-item-title v-if="option.value === 'defineCustomTemplate'">{{ option.title }}</v-list-item-title>
            <v-list-item-action v-else>
              <v-radio :value="option.value" :label="option.title" />
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-radio-group>
      <template v-slot:empty>
        No more results
      </template>
    </v-infinite-scroll>
  </div>
  <div v-if="showDetails">
    <spinner-component text="Loading, please wait..." v-if="loadingTemplate"></spinner-component>
    <div v-else-if="!loadingTemplate && template && Object.keys(template).length > 0 ">
      <div class="d-flex justify-space-between">
        <div class="text-subtitle-1">Template Name: <span class="text-subtitle-1 font-weight-bold">{{ template['templateName'] }}</span></div>
        <div v-if="showEdit && template && template['templateId']">
          <router-link v-if="template" :to="{name: 'account.scans.templates.edit', params: {templateID: template['templateId']}}" target="_blank" class="text-black">
            <span><v-icon size="20" class="px-2">mdi-pencil</v-icon>Edit</span>
          </router-link>
        </div>
      </div>
      <div><p>created on: {{ formatDate(template['createdTimestamp'], 'lll') }}</p></div>
      <v-card class="rounded-1 pa-3 mt-2" variant="outlined">
        <v-card-text>
          <div class="text-subtitle mb-2">{{ fieldContainerTitle }}</div>
          <span class="text-h6" v-if="!template['field']">This template contains no columns</span>
          <div v-for="field in template['field']" :key="field.fieldName" class="list_template_cell">
            <div v-if="field.newColHeader != ''"><strong>{{field.newColHeader}}</strong></div>
            <div v-if="field.newColHeader == ''">
              <div v-if="field.fieldType == 'system'"><strong>{{field.fieldName}}</strong></div>
              <div v-else-if="field.fieldType == 'questions'">Question: <strong>{{field.fieldName}}</strong></div>
              <div v-else-if="field.fieldType == 'properties'">Property: <strong>{{field.fieldName}}</strong></div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, watch } from 'vue';
import crApi from '../service/CrApi';
import securityService from '../service/SecurityService';
import { useRouter } from "vue-router";
import moment from "moment";
import SpinnerComponent from "./SpinnerComponent.vue";

const props = defineProps({
  allowCreate: {
    type: Boolean,
    default: true,
  },
  additionalOptions: {
    type: [Object, Array],
    default: () => ({}),
  },
  showDetails: {
    type: Boolean,
    default: true,
  },
  fieldContainerTitle: {
    type: String,
    default: 'Fields:',
  },
  showTemplateOptions: {
    type: Boolean,
    default: true,
  },
  value: {
    type: String,
    default: '',
  },
  prefix: {
    type: [String, Number],
    default: '',
  },
  showEdit: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: 'Output Format:'
  },
  modelValue: {
    type: String,
    default: ''
  },
  isAutomaticllyTamplate: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['update:modelValue']);

const router = ref(useRouter());
const templateNames = ref([]);
const isLoading = ref(false);
const allLoaded = ref(false);
const template = ref({});
const loadingTemplate = ref(false);
const isDeleted = ref(false);
const adminParams = ref({});
const idPrefix = ref('');
const allowCreate = ref(props.allowCreate);
const showDetails = ref(props.showDetails);
const showEdit = ref(props.showEdit);
const additionalOptions = ref(props.additionalOptions);
const fieldContainerTitle = ref(props.fieldContainerTitle);
const offset = ref(0);
const limit = ref(25);

const internalModelValue = ref(props.modelValue);

watch(() => props.modelValue, async () => {
  if (props.modelValue) {
    internalModelValue.value = props.modelValue;
    await loadTemplateDetails();
  }
});

watch(internalModelValue, (newVal) => {
  emit('update:modelValue', newVal);
});

onBeforeMount(async () => {
  await loadTemplates();
  if (props.modelValue) {
    await loadTemplateDetails();
  }
});

const allTemplateNames = computed(() => {
  let a = [];
  if (isDeleted.value) {
    a.push({ value: internalModelValue.value, title: 'TEMPLATE DELETED!' });
  }
  for (let k of Object.keys(props.additionalOptions)) {
    a.push({ value: k, title: props.additionalOptions[k] });
  }
  if (props.allowCreate) {
    a.push({ value: 'defineCustomTemplate', title: '-- Create New Template' });
  }
  for (let q of templateNames.value) {
    a.push({ value: q.templateId, title: q.templateName });
  }
  return a;
});

async function loadTemplates() {
  isLoading.value = true;
  try {
    let response = await crApi.requestV2('export_template/retrieve/', {
      ...adminParams.value,
      'website_token': securityService.getToken(),
      limit: limit.value,
      offset: offset.value,
      isAutomaticllyTamplate: props.isAutomaticllyTamplate
    });

    if (response.status == 1) {
      const newTemplates = response.template.filter(template => !templateNames.value.some(t => t['templateId'] === template['templateId']));
      templateNames.value.push(...newTemplates);
      offset.value += response.template.length;

      if (response.template.length < limit.value) {
        allLoaded.value = true; // No more items to load
      }
    }
  } catch (error) {
    console.error('Error loading templates:', error);
  } finally {
    isLoading.value = false;
  }
}

async function loadMore({ done }: { done?: (arg: string) => void } = {}) {
  if (isLoading.value || allLoaded.value) {
    done('empty');
    return;
  }
  isLoading.value = true;

  try {
    const response = await crApi.requestV2('export_template/retrieve/', {
      sortBy: 'templateName',
      response: 'extra',
      sortOrder: 'asc',
      website_token: securityService.getToken(),
      offset: offset.value,
      limit: limit.value,
      isAutomaticllyTamplate: props.isAutomaticllyTamplate
    });

    if (response && response.status === 1) {
      const newTemplates = response.template.filter(template => !templateNames.value.some(t => t['templateId'] === template['templateId']));
      templateNames.value.push(...newTemplates);
      offset.value += response.template.length;

      if (done) {
        done(response.template.length === 0 ? 'empty' : 'done');
      }
    }
  } catch (error) {
    console.error('Error loading templates:', error);
  } finally {
    isLoading.value = false;
  }
}

async function loadTemplateDetails() {
  if (!internalModelValue.value) {
    return;
  }

  loadingTemplate.value = true;

  try {
    let templateParams = { ...adminParams.value, 'templateId': internalModelValue.value, isAutomaticllyTamplate: props.isAutomaticllyTamplate, 'response': 'extra', 'website_token': securityService.getToken() };
    let response = await crApi.requestV2('export_template/retrieve/', templateParams);

    if (response.template.length < 1) {
      template.value = null;
      if (!['regwithprops', 'regular', '0'].includes(internalModelValue.value)) {
        isDeleted.value = true;
      }
      loadingTemplate.value = false;
      return;
    }

    template.value = response.template[0];
    //@ts-ignore
    let fields = template.value['field'].map((field) => {
      let modifications = field['modifications'] || {};
      return {
        fieldName: field.fieldName,
        newColHeader: modifications.newColHeader ?? '',
        fieldType: field.fieldType,
      };
    });

    template.value['field'] = fields;
    isDeleted.value = false;
  } catch (error) {
    console.error('Error loading template details:', error);
  } finally {
    loadingTemplate.value = false;
  }
}

function selectTemplate(option) {
  if (option.value === 'defineCustomTemplate') {
    router.value.push({ name: 'account.scans.templates.new' });
  } else {
    internalModelValue.value = option.value;
  }
}

function formatDate(date, format) {
  return moment(date).format(format);
}
</script>

<style scoped>
.list_template_cell {
  margin-bottom: 8px;
}
.template-select {
  max-height: 400px; /* Set a max height for the dropdown */
  overflow-y: auto;
}
</style>
